import * as React from 'react';
import addDays from 'date-fns/addDays';
import Typography from '@mui/material/Typography';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
    getNextDay,
    getPreviousDay,
    isPreviousDayBeforeEarliestBookableDay,
    PHOTOGRAPHER_BOOKING_DATE_FORMAT
} from '../state/dateHelper';
import { Hint } from '../../view/Hint';
import { Translate, translateDateFormat } from '../../../../../../../shared/LanguageService';

type PhotographerBookingDateSelectProps = {
    showHint: boolean;
    bookingDate: Date;
    disabled: boolean;
    onChange: (newDate: Date) => void;
    earliestBookableDate?: Date | null;
};

export function BookingDateSelect({
    showHint,
    bookingDate,
    disabled,
    onChange,
    earliestBookableDate = null
}: PhotographerBookingDateSelectProps) {
    let isPreviousDayDisabled = isPreviousDayBeforeEarliestBookableDay(
        bookingDate,
        earliestBookableDate
    );

    function handleClickNextDay() {
        onChange(getNextDay(bookingDate));
    }

    function handleClickPreviousDay() {
        if (isPreviousDayDisabled) {
            return;
        }

        let previousDay = getPreviousDay(bookingDate);
        onChange(previousDay);
    }

    const today = new Date(Date.now());
    return (
        <Box
            component="section"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                paddingBottom: '12px',
                width: '100%'
            }}
        >
            <Hint showHint={showHint}>
                {Translate('se360/photographerbooking/customer/calendar/date-hint')}
            </Hint>
            <Card
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px',
                    cursor: 'pointer'
                }}
            >
                <IconButton
                    disabled={disabled ? true : isPreviousDayDisabled}
                    aria-label="Select previous day"
                    onClick={handleClickPreviousDay}
                >
                    <ArrowBackIosNewIcon />
                </IconButton>
                <MobileDatePicker
                    label={Translate('se360/dusk-photographer-booking/request-modal-date')}
                    aria-label={Translate('se360/dusk-photographer-booking/request-modal-date')}
                    disabled={disabled}
                    inputFormat={PHOTOGRAPHER_BOOKING_DATE_FORMAT}
                    minDate={
                        earliestBookableDate !== null ? earliestBookableDate : addDays(today, 1)
                    }
                    value={bookingDate}
                    disablePast={true}
                    sx={{
                        '.MuiPickersToolbar-penIconButton': {
                            display: 'none'
                        }
                    }}
                    components={{
                        // @ts-ignore
                        ActionBar: () => null
                    }}
                    onChange={(newDate) => {
                        onChange(newDate);
                    }}
                    renderInput={({ inputRef, inputProps }) => {
                        let formattedDate = translateDateFormat(
                            bookingDate,
                            PHOTOGRAPHER_BOOKING_DATE_FORMAT
                        );
                        return (
                            <Box
                                ref={inputRef}
                                {...inputProps}
                                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                    if (disabled) {
                                        event.preventDefault();
                                    } else if (
                                        inputProps &&
                                        typeof inputProps.onClick === 'function'
                                    ) {
                                        inputProps.onClick(event);
                                    }
                                }}
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    padding: '0 20px'
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontFamily: 'Gilroy',
                                        fontSize: '1.8rem',
                                        fontWeight: '700',
                                        color: disabled ? '#aaa' : 'text.secondary',
                                        textTransform: 'capitalize',
                                        userSelect: 'none'
                                    }}
                                >
                                    <time dateTime={formattedDate}>{formattedDate}</time>
                                </Typography>
                                <Box sx={{ flex: '1 auto' }} />
                                <CalendarMonthIcon
                                    sx={{
                                        color: disabled ? '#aaa' : 'text.primary'
                                    }}
                                />
                            </Box>
                        );
                    }}
                />
                <Box sx={{ flex: '1 auto' }} />
                <IconButton disabled={disabled} onClick={handleClickNextDay} aria-label="Select next day">
                    <ArrowForwardIosIcon />
                </IconButton>
            </Card>
        </Box>
    );
}
